import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';
import { CategoryMark } from '@/components/CategoryMark';

interface PaymentShare {
  user_id: string;
  share_percentage: number;
  share_amount: number;
}

interface Payment {
  id: string;
  description: string;
  amount: number;
  paid_at: string;
  paid_by: string;
  paid_by_name: string;
  category_id: string;
  category_name: string;
  category_icon: string;
  is_settled: boolean;
  is_instant_request: boolean;
  shares: PaymentShare[];
}

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentYearMonth, setCurrentYearMonth, categories, showToast } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // 選択モード関連
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);

  // 割合変更モーダル
  const [showShareModal, setShowShareModal] = useState(false);
  const [newSharePercentage, setNewSharePercentage] = useState(50);

  useEffect(() => {
    loadPayments();
  }, [currentYearMonth, selectedCategory]);

  // 選択モード解除時に選択をクリア
  useEffect(() => {
    if (!isSelectMode) {
      setSelectedIds(new Set());
    }
  }, [isSelectMode]);

  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const res = await api.getPayments({
        yearMonth: currentYearMonth,
        categoryId: selectedCategory || undefined,
      });
      setPayments(res.payments);
    } catch (error) {
      console.error('Failed to load payments:', error);
      showToast('支出の読み込みに失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    if (!confirm(`選択した${selectedIds.size}件の支出を削除しますか？`)) return;

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedIds).map(id => api.deletePayment(id));
      await Promise.all(deletePromises);

      setPayments(payments.filter(p => !selectedIds.has(p.id)));
      showToast(`${selectedIds.size}件の支出を削除しました`, 'success');
      setSelectedIds(new Set());
      setIsSelectMode(false);
    } catch (error) {
      showToast('削除に失敗しました', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangeSharePercentage = async () => {
    if (selectedIds.size === 0) return;

    setShowShareModal(false);
    setIsDeleting(true); // ローディング表示に再利用

    try {
      const updatePromises = Array.from(selectedIds).map(id =>
        api.updatePayment(id, { sharePercentage: newSharePercentage })
      );
      await Promise.all(updatePromises);

      showToast(`${selectedIds.size}件の負担割合を変更しました`, 'success');
      setSelectedIds(new Set());
      setIsSelectMode(false);
      loadPayments(); // 再読み込み
    } catch (error) {
      showToast('負担割合の変更に失敗しました', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLongPressStart = (payment: Payment) => {
    // 未精算かつ自分が支払った場合のみ選択可能
    if (payment.is_settled || payment.paid_by !== user?.id) return;

    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setIsSelectMode(true);
      setSelectedIds(new Set([payment.id]));
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handlePaymentClick = (payment: Payment) => {
    // 長押し後のクリックは無視
    if (isLongPress.current) {
      isLongPress.current = false;
      return;
    }

    if (isSelectMode) {
      // 選択モード中
      const canSelect = !payment.is_settled && payment.paid_by === user?.id;
      if (!canSelect) return;

      const newSelectedIds = new Set(selectedIds);
      if (selectedIds.has(payment.id)) {
        newSelectedIds.delete(payment.id);
        if (newSelectedIds.size === 0) {
          setIsSelectMode(false);
        }
      } else {
        newSelectedIds.add(payment.id);
      }
      setSelectedIds(newSelectedIds);
    } else {
      // 通常モード - 詳細・編集画面へ遷移
      navigate(`/payments/${payment.id}/edit`);
    }
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  const changeMonth = (delta: number) => {
    const [year, month] = currentYearMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + delta, 1);
    const newYearMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
    setCurrentYearMonth(newYearMonth);
  };

  const formatYearMonth = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-');
    return `${year}年${parseInt(month)}月`;
  };

  // 日付でグループ化
  const groupedPayments = payments.reduce(
    (acc, payment) => {
      const date = payment.paid_at;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(payment);
      return acc;
    },
    {} as Record<string, Payment[]>
  );

  const sortedDates = Object.keys(groupedPayments).sort((a, b) => b.localeCompare(a));

  // 選択可能な支出の数（未精算かつ自分が支払った）
  const selectableCount = payments.filter(p => !p.is_settled && p.paid_by === user?.id).length;

  return (
    <div className="pb-4">
      {/* ヘッダー */}
      <div className="page-topbar">
        {/* 選択モード時のヘッダー */}
        {isSelectMode ? (
          <div className="px-5 py-3 bg-primary-50">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSelectMode(false)}
                className="text-gray-600 font-medium"
              >
                キャンセル
              </button>
              <span className="font-bold text-primary-700">
                {selectedIds.size}件選択中
              </span>
              <div className="w-16"></div>
            </div>
          </div>
        ) : (
          <>
            {/* 年月選択 */}
            <div className="page-topbar-inner">
              <button
                onClick={() => changeMonth(-1)}
                className="icon-button"
                aria-label="前の月"
              >
                ←
              </button>
              <h1 className="text-lg font-bold">{formatYearMonth(currentYearMonth)}</h1>
              <button
                onClick={() => changeMonth(1)}
                className="icon-button"
                aria-label="次の月"
              >
                →
              </button>
            </div>

            {/* カテゴリフィルター */}
            <div className="px-5 pb-3 flex gap-2 overflow-x-auto">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${!selectedCategory
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700'
                  }`}
              >
                すべて
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1 ${selectedCategory === category.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                    }`}
                >
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 長押しヒント（選択可能な支出がある場合、選択モードでないとき） */}
      {!isSelectMode && selectableCount > 0 && (
        <div className="mx-4 mt-3 px-3 py-2.5 bg-primary-50 text-primary-700 text-xs rounded-xl flex items-center justify-center gap-2">
          <AppIcon name="spark" size={15} />
          未精算の支出を長押しで複数選択できます
        </div>
      )}

      {/* 支出リスト */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12">
          <div className="empty-icon mx-auto mb-3"><AppIcon name="receipt" size={22} /></div>
          <p className="text-gray-500">支出がありません</p>
          <button
            onClick={() => navigate('/payments/new')}
            className="btn btn-primary btn-md mt-4"
          >
            支出を追加
          </button>
        </div>
      ) : (
        <div className={isSelectMode ? 'pb-24' : ''}>
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 sticky top-[108px]">
                {new Date(date).toLocaleDateString('ja-JP', {
                  month: 'short',
                  day: 'numeric',
                  weekday: 'short',
                })}
              </div>
              <div className="divide-y divide-gray-100">
                {groupedPayments[date].map((payment) => {
                  const isMyPayment = payment.paid_by === user?.id;
                  const canSelect = isMyPayment && !payment.is_settled;
                  const isSelected = selectedIds.has(payment.id);
                  // 自分の負担割合を取得
                  const myShare = payment.shares?.find(s => s.user_id === user?.id);
                  const myPercentage = myShare?.share_percentage ?? 50;

                  return (
                    <div
                      key={payment.id}
                      className={`list-item cursor-pointer active:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''} ${isSelectMode && !canSelect ? 'opacity-50' : ''}`}
                      onClick={() => handlePaymentClick(payment)}
                      onMouseDown={() => handleLongPressStart(payment)}
                      onMouseUp={handleLongPressEnd}
                      onMouseLeave={handleLongPressEnd}
                      onTouchStart={() => handleLongPressStart(payment)}
                      onTouchEnd={handleLongPressEnd}
                      onTouchCancel={handleLongPressEnd}
                    >
                      {/* 選択モード時のチェックボックス */}
                      {isSelectMode && (
                        <div className="mr-3">
                          {canSelect ? (
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'border-gray-300'
                              }`}>
                              {isSelected && '✓'}
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-200 bg-gray-100"></div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-3 flex-1">
                        <CategoryMark name={payment.category_name} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{payment.description}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            {/* 入力者バッジ */}
                            <span className={`badge ${isMyPayment ? 'badge-primary' : 'badge-secondary'}`}>
                              {isMyPayment ? 'わたし' : 'パートナー'}
                            </span>
                            {!!payment.is_instant_request && (
                              <span className="badge badge-warning">即時精算</span>
                            )}
                            {!!payment.is_settled && (
                              <span className="badge badge-success">精算済み</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-gray-500">
                          {myPercentage}% 負担
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 選択モード時の操作バー */}
      {isSelectMode && (
        <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex gap-3 z-20">
          <button
            onClick={() => {
              setNewSharePercentage(50);
              setShowShareModal(true);
            }}
            disabled={isDeleting || selectedIds.size === 0}
            className="flex-1 btn btn-secondary py-3 disabled:opacity-50"
          >
            <AppIcon name="sliders" size={18} />
            割合変更
          </button>
          <button
            onClick={handleDeleteSelected}
            disabled={isDeleting || selectedIds.size === 0}
            className="flex-1 btn bg-red-500 text-white py-3 disabled:opacity-50 hover:bg-red-600"
          >
            {isDeleting ? '処理中...' : <><AppIcon name="trash" size={18} />削除</>}
          </button>
        </div>
      )}

      {/* 割合変更モーダル */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-4 text-center">負担割合を変更</h3>
            <p className="text-sm text-gray-500 text-center mb-4">
              選択した{selectedIds.size}件の支出の割合を一括変更します
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                自分の負担割合: {newSharePercentage}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={newSharePercentage}
                onChange={(e) => setNewSharePercentage(parseInt(e.target.value))}
                className="slider w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
              <p className="text-center mt-2 text-sm">
                相手の負担: {100 - newSharePercentage}%
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 btn btn-secondary py-3"
              >
                キャンセル
              </button>
              <button
                onClick={handleChangeSharePercentage}
                className="flex-1 btn btn-primary py-3"
              >
                変更する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 追加ボタン（フローティング） - 選択モードでは非表示 */}
      {!isSelectMode && (
        <button
          onClick={() => navigate('/payments/new')}
          className="floating-add-button fixed right-4 bottom-24"
          aria-label="支出を追加"
        >
          <AppIcon name="plus" size={24} />
        </button>
      )}
    </div>
  );
}
